import React, { useState, useEffect } from 'react'
import { Mic, Play } from 'lucide-react'
import logger from './utils/logger.js';


export default function AudioVisualizer({ setAudioQuality }) {
  const [audioStrength, setAudioStrength] = useState(0)
  const [isRecording, setIsRecording] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioBlob, setAudioBlob] = useState(null)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [width, setWidth] = useState(window.innerWidth)
  const totalBars = width < 768 ? 25 : 50
  let intervalId = null

  useEffect(() => {
    // Request microphone access when component mounts
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        const recorder = new MediaRecorder(stream)
        const chunks = []

        recorder.ondataavailable = (e) => chunks.push(e.data)
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' })
          setAudioBlob(blob)
          setIsRecording(false) // Stop recording state
        }

        setMediaRecorder(recorder)
      })
      .catch(err => logger.error('Error accessing microphone:', err))

    const handleResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleRecord = () => {
    if (!mediaRecorder) return

    setIsRecording(true)
    setAudioBlob(null)
    mediaRecorder.start()

    // Start the audio visualization
    intervalId = setInterval(() => {
      const strength = Math.floor(Math.random() * (totalBars + 1))
      setAudioStrength(strength)
      evaluateAudioQuality(strength)
    }, 1000)

    // Stop recording after 4 seconds
    setTimeout(() => {
      mediaRecorder.stop()
      clearInterval(intervalId)
      setAudioStrength(0)
    }, 4000) // Adjust the duration as needed
  }

  const handlePlay = async () => {
    if (!audioBlob) return

    setIsPlaying(true)
    const audio = new Audio(URL.createObjectURL(audioBlob))
    audio.onended = () => setIsPlaying(false)
    audio.play()

    // Simulate visualization during playback
    intervalId = setInterval(() => {
      const strength = Math.floor(Math.random() * (totalBars + 1))
      setAudioStrength(strength)
      evaluateAudioQuality(strength)
    }, 1000)
  }

  const getBarColor = (index) => {
    if (index >= audioStrength) return 'bg-gray-400'
    if (index < 12) return 'bg-red-500'
    if (index < 24) return 'bg-orange-500'
    return 'bg-green-500'
  }

  const evaluateAudioQuality = (strength) => {
    if (strength < 12) {
      setAudioQuality("Weak");
    } else if (strength < 24) {
      setAudioQuality("Good");
    } else {
      setAudioQuality("Excellent");
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`${width < 768 ? 'w-[330px]' : 'w-[500px]'} h-[16px] flex items-center gap-1`}>
        <Mic className={`${width < 768 ? 'h-4 w-4' : 'h-6 w-6'} text-gray-400`} />
        <div className="flex-1 h-full flex items-center gap-[1px]">
          {[...Array(totalBars)].map((_, index) => (
            <div
              key={index}
              className={`h-full flex-1 ${getBarColor(index)} transition-colors duration-300 rounded-sm`}
              role="presentation"
              aria-hidden="true"
            />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRecord}
            disabled={isRecording || isPlaying}
            className={`flex items-center justify-center ${width < 768 ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-md transition-colors ${
              isRecording
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700'
            }`}
          >
            <Mic className={`${width < 768 ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            {isRecording ? 'Recording...' : 'Record'}
          </button>
          <button
            onClick={handlePlay}
            disabled={!audioBlob || isPlaying}
            className={`flex items-center justify-center ${width < 768 ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-md transition-colors ${
              !audioBlob || isPlaying
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
            }`}
          >
            <Play className={`${width < 768 ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            Play
          </button>
        </div>
      </div>
    </div>
  )
}

